import React, { useEffect, useRef, useState } from 'react';

const MAX_VISIBLE_CHIPS = 5;
const MID_DESKTOP_BREAKPOINT = 1500;
const OVERFLOW_DROPDOWN_ID = 'fp-overflow-dropdown';

const getVisibleChipsByViewport = () =>
    typeof window !== 'undefined' && window.innerWidth <= MID_DESKTOP_BREAKPOINT ? 3 : MAX_VISIBLE_CHIPS;

const FundPortfolioTabs = ({ funds, currentTab, onTabChange }) => {
    const [isOverflowOpen, setIsOverflowOpen] = useState(false);
    const [maxVisibleChips, setMaxVisibleChips] = useState(getVisibleChipsByViewport);
    const overflowRef = useRef(null);
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!isOverflowOpen) return undefined;
        const handleOutsideClick = (e) => {
            if (overflowRef.current && !overflowRef.current.contains(e.target)) {
                setIsOverflowOpen(false);
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setIsOverflowOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOverflowOpen]);

    useEffect(() => {
        const handleResize = () => setMaxVisibleChips(getVisibleChipsByViewport());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const othersEntry = funds[funds.length - 1]?.id === 'others' ? funds[funds.length - 1] : null;
    const othersTabIndex = othersEntry ? funds.length - 1 : -1;
    const regularFunds = othersEntry ? funds.slice(0, -1) : funds;
    const visibleFunds = regularFunds.slice(0, maxVisibleChips);
    const overflowFunds = regularFunds.slice(maxVisibleChips);
    const isOverflowSelected = currentTab >= maxVisibleChips && currentTab < (othersEntry ? funds.length - 1 : funds.length);
    const selectedOverflowFund = isOverflowSelected ? overflowFunds[currentTab - maxVisibleChips] : null;
    const overflowTriggerLabel = selectedOverflowFund ? selectedOverflowFund.name : `+${overflowFunds.length} funds`;
    // A fund with no startup still shows its 0 badge, as the Direct Investments fund tabs do
    const overflowTriggerCount = selectedOverflowFund?.companies_investedin_count ?? null;

    const handleSelect = (index) => {
        setIsOverflowOpen(false);
        onTabChange(index);
    };

    return (
        <div className="fund-portfolio-tabs">
            {visibleFunds.map((tab, i) => (
                <button
                    key={tab.id}
                    type="button"
                    className={[
                        'fund-portfolio-chip',
                        currentTab === i ? 'fund-portfolio-chip--active' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSelect(i)}
                >
                    <span>{tab.name}</span>
                    {tab.companies_investedin_count != null && (
                        <span className="fund-portfolio-chip__badge">{tab.companies_investedin_count}</span>
                    )}
                </button>
            ))}

            {overflowFunds.length > 0 && (
                <div ref={overflowRef} className="fund-portfolio-overflow">
                    <button
                        ref={triggerRef}
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={isOverflowOpen}
                        aria-controls={OVERFLOW_DROPDOWN_ID}
                        className={[
                            'fund-portfolio-chip',
                            'fund-portfolio-chip--overflow',
                            isOverflowOpen ? 'fund-portfolio-chip--open' : '',
                            isOverflowSelected ? 'fund-portfolio-chip--active' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => setIsOverflowOpen((prev) => !prev)}
                    >
                        <span>{overflowTriggerLabel}</span>
                        {overflowTriggerCount != null && (
                            <span className="fund-portfolio-chip__badge">{overflowTriggerCount}</span>
                        )}
                        <i className={`fa ${isOverflowOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
                    </button>

                    {isOverflowOpen && (
                        <div
                            id={OVERFLOW_DROPDOWN_ID}
                            role="listbox"
                            className="fund-portfolio-dropdown"
                        >
                            {overflowFunds.map((tab, i) => {
                                const tabIndex = maxVisibleChips + i;
                                return (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        role="option"
                                        aria-selected={currentTab === tabIndex}
                                        className={[
                                            'fund-portfolio-chip',
                                            currentTab === tabIndex ? 'fund-portfolio-chip--active' : '',
                                        ].filter(Boolean).join(' ')}
                                        onClick={() => handleSelect(tabIndex)}
                                    >
                                        <span>{tab.name}</span>
                                        {tab.companies_investedin_count != null && (
                                            <span className="fund-portfolio-chip__badge">{tab.companies_investedin_count}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {othersEntry && (
                <button
                    type="button"
                    className={[
                        'fund-portfolio-chip',
                        'fund-portfolio-chip--others',
                        currentTab === othersTabIndex ? 'fund-portfolio-chip--active' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSelect(othersTabIndex)}
                >
                    <span>{othersEntry.name}</span>
                    {othersEntry.companies_investedin_count > 0 && (
                        <span className="fund-portfolio-chip__badge">{othersEntry.companies_investedin_count}</span>
                    )}
                </button>
            )}
        </div>
    );
};

export default FundPortfolioTabs;
